import type { Experiment } from "@/types/experiment";
import type { Plot } from "@/types/plot";

export interface PlotMetadataEntry {
  label: string;
  value: string;
}

/**
 * The extra plot map columns a user uploaded for a plot (variety, treatment,
 * rep, ...), ready to display as label/value rows.
 *
 * Ordered by `plotMap.headers` so the fields read in the same order as the
 * user's spreadsheet. Keys missing from `headers` are appended rather than
 * dropped: an experiment's declared headers can drift from what its plots
 * actually store (differing case, columns added by a later metadata upload),
 * and silently hiding that plot's data would be worse than showing it out of
 * order.
 *
 * The plot ID / row / column columns are excluded - the heatmap tooltip already
 * shows those above. Both CSV write paths in `services/firebase.ts` strip them
 * before saving, so this only matters for older documents.
 */
export function getPlotMetadataEntries(plot: Plot, plotMap?: Experiment["plotMap"]): PlotMetadataEntry[] {
  const metadata = plot.metadata;
  if (!metadata) return [];

  const positionalColumns = new Set(
    [plotMap?.plotIDName, plotMap?.rowIDName, plotMap?.colIDName].filter((name): name is string => !!name)
  );

  // Declared headers first (spreadsheet order), then any stored key they missed.
  // The Set drops the duplicates that join produces.
  return [...new Set([...(plotMap?.headers ?? []), ...Object.keys(metadata)])]
    .filter((label) => !positionalColumns.has(label))
    .map((label) => ({
      label,
      value: typeof metadata[label] === "string" ? metadata[label].trim() : "",
    }))
    .filter((entry) => entry.value !== "");
}
